// libs
import * as z from 'zod'

/**
 * Zod schema of Chart.config, shared with the backend: it imports nothing but zod (4.6.5 on both sides), so the
 * file is copied as is. Edit it in the frontend only.
 *
 * - The schema is strict: a wrong value or an unknown key is an error, never quietly replaced or dropped. The backend
 *   checks every saved config with it, so a stored config always parses.
 * - A missing field takes its default, so adding an option needs nothing else. Removing an option or changing what a
 *   value may be (an enum value removed, a range narrowed) breaks stored configs: bump version and migrate them.
 * - What the chart counts (Chart.query) is not here: the server checks it by its own rules. GROUP_BY_COUNTS is the
 *   one rule that joins the two.
 *
 * Meta beyond title and description, under SCHEMA_KEYS: chartTypes limits a property or a section to some chart
 * types, widget picks a non-default form control.
 */

export enum ChartType {
  Bar = 'BAR',
  Line = 'LINE',
  Pie = 'PIE'
}

// How many fields of Chart.query.groupBy a chart type takes: the first gives the categories (or slices), the second
// splits them into series
export const GROUP_BY_COUNTS: Record<ChartType, number[]> = {
  [ChartType.Bar]: [1, 2],
  [ChartType.Line]: [1, 2],
  [ChartType.Pie]: [1]
}

// The values of the chart config's options: the schema, the form and the drawing read them from here

export enum ChartSortBy {
  // Dates in time order, options in their order in the field, other keys by label
  None = 'NONE',
  Label = 'LABEL',
  Value = 'VALUE'
}

export enum ChartSortOrder {
  Asc = 'ASC',
  Desc = 'DESC'
}

export enum ChartValueMode {
  Absolute = 'ABSOLUTE',
  // Share of the total of all series
  Percent = 'PERCENT',
  // Running total along the categories
  Cumulative = 'CUMULATIVE'
}

export enum ChartAlign {
  Left = 'left',
  Center = 'center',
  Right = 'right'
}

export enum ChartSide {
  Top = 'top',
  Bottom = 'bottom',
  Left = 'left',
  Right = 'right'
}

export enum ChartLegendType {
  Plain = 'plain',
  // Pages a long legend instead of wrapping it
  Scroll = 'scroll'
}

export enum ChartLegendAlign {
  Auto = 'auto',
  Left = 'left',
  Right = 'right'
}

export enum ChartLegendIcon {
  Circle = 'circle',
  Rect = 'rect',
  RoundRect = 'roundRect',
  Line = 'line'
}

export enum ChartTooltipTrigger {
  // One bar / point / slice
  Item = 'item',
  // Every series at the hovered category
  Axis = 'axis'
}

export enum ChartAxisPointer {
  Line = 'line',
  Shadow = 'shadow',
  Cross = 'cross',
  None = 'none'
}

export enum ChartPalette {
  Default = 'DEFAULT',
  Vivid = 'VIVID',
  Pastel = 'PASTEL',
  Cool = 'COOL',
  Warm = 'WARM',
  Mono = 'MONO',
  // The colors of display.colors.custom
  Custom = 'CUSTOM'
}

export enum ChartLabelPosition {
  Top = 'top',
  Inside = 'inside',
  InsideTop = 'insideTop',
  InsideBottom = 'insideBottom',
  // A pie's label next to its slice
  Outside = 'outside',
  Right = 'right',
  Left = 'left'
}

export enum ChartLabelFormat {
  Value = 'VALUE',
  // Share of the series; in a bar stacked to 100%, of the category
  Percent = 'PERCENT',
  Name = 'NAME',
  NameValue = 'NAME_VALUE',
  NamePercent = 'NAME_PERCENT'
}

export enum ChartNameLocation {
  Start = 'start',
  Middle = 'middle',
  End = 'end'
}

export enum ChartLabelInterval {
  // Skips the labels that overlap
  Auto = 'auto',
  All = 'all'
}

export enum ChartScale {
  Value = 'value',
  Log = 'log'
}

export enum ChartStack {
  // Series side by side
  None = 'NONE',
  Stacked = 'STACKED',
  // Stacked to 100%
  Percent = 'PERCENT'
}

export enum ChartLineStep {
  None = 'NONE',
  Start = 'start',
  Middle = 'middle',
  End = 'end'
}

export enum ChartSymbol {
  EmptyCircle = 'emptyCircle',
  Circle = 'circle',
  Rect = 'rect',
  RoundRect = 'roundRect',
  Triangle = 'triangle',
  Diamond = 'diamond',
  Pin = 'pin',
  Arrow = 'arrow'
}

export enum ChartPieVariant {
  Pie = 'PIE',
  // A hole in the middle
  Donut = 'DONUT',
  // Nightingale rose: the slice radius shows the value
  RoseRadius = 'ROSE_RADIUS',
  // Nightingale rose: the slice area shows the value
  RoseArea = 'ROSE_AREA'
}

export enum ChartSelectedMode {
  None = 'none',
  Single = 'single',
  Multiple = 'multiple'
}

// Keys of our own in the JSON Schema, x- prefixed as the standard asks
export const SCHEMA_KEYS = {
  chartTypes: 'x-chartTypes',
  widget: 'x-widget'
} as const

export enum SchemaWidget {
  // A list of hex colors
  Colors = 'colors'
}

export interface FieldMeta {
  [SCHEMA_KEYS.chartTypes]?: ChartType[]
  [SCHEMA_KEYS.widget]?: SchemaWidget
}

// Every text of the config: titles, names, labels, affixes
const TEXT_MAX_LENGTH = 200

const CUSTOM_COLORS_MAX = 20

// Without the i flag: JSON Schema patterns carry none
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

const onlyFor = (chartTypes: ChartType[]): FieldMeta => ({ [SCHEMA_KEYS.chartTypes]: chartTypes })

const AXIS_CHARTS = [ChartType.Bar, ChartType.Line]

const bool = (title: string, description: string, value: boolean, meta?: FieldMeta) =>
  z
    .boolean()
    .default(value)
    .meta({ title, description, ...meta })

const limit = (minimum?: number, maximum?: number) => {
  let number = z.number()

  if (minimum !== undefined) {
    number = number.min(minimum)
  }
  if (maximum !== undefined) {
    number = number.max(maximum)
  }

  return number
}

const num = (title: string, description: string, value: number, minimum?: number, maximum?: number) =>
  limit(minimum, maximum).default(value).meta({ title, description })

// Empty is no number: Top N keeps all categories, an axis fits the data
const optionalNum = (title: string, description: string, minimum?: number, maximum?: number) =>
  limit(minimum, maximum).nullable().default(null).meta({ title, description })

// Without a value the text is optional: empty means "use the fallback", e.g. the widget title
const str = (title: string, description: string, value?: string) =>
  value === undefined
    ? z.string().max(TEXT_MAX_LENGTH).optional().meta({ title, description })
    : z.string().max(TEXT_MAX_LENGTH).default(value).meta({ title, description })

// One value of a string enum of this file, in the enum's order
const oneOf = <T extends Record<string, string>>(
  title: string,
  description: string,
  values: T,
  value: T[keyof T],
  meta?: FieldMeta
) =>
  z
    .enum(values)
    // A generic enum's value is not provably its default's type
    .default(value as never)
    .meta({ title, description, ...meta })

// A missing section is filled with its defaults
const section = <T extends z.ZodRawShape>(title: string, description: string, shape: T, meta?: FieldMeta) => {
  const object = z.strictObject(shape)

  return object.prefault({} as z.input<typeof object>).meta({ title, description, ...meta })
}

const ChartTransformSchema = section(
  'Sorting & limits',
  'What the app does with the rows before drawing them. They come without gaps: every option, switch value and period between the first and the last, at 0 for COUNT and SUM',
  {
    sortBy: oneOf(
      'Sort by',
      'Order of the categories. NONE: dates in time order, options in their order in the field, other keys by label',
      ChartSortBy,
      ChartSortBy.None
    ),
    sortOrder: oneOf('Sort order', 'Ascending or descending', ChartSortOrder, ChartSortOrder.Desc),
    topN: optionalNum('Top N', 'Keep only the first N categories after sorting; empty keeps all', 1, 100),
    groupOthers: bool(
      'Group the rest',
      'With Top N, join the cut categories into one "Others" category. For AVG it is the plain average of their averages',
      false
    ),
    othersLabel: str('"Others" label', 'Name of the category the rest is grouped into', 'Others'),
    hideEmpty: bool('Hide "no value"', 'Drop the group of records with no value in the grouped field', false),
    emptyLabel: str('"No value" label', 'Name of the group of records with no value', '(No value)'),
    unknownLabel: str(
      '"Unknown" label',
      'Name of a category the app cannot name: an option since removed from the field, a deleted record',
      '(Unknown)'
    ),
    valueMode: oneOf(
      'Values as',
      'ABSOLUTE: the numbers; PERCENT: share of the total of all series; CUMULATIVE: running total along the categories in their order, so use it with Sort by NONE or LABEL',
      ChartValueMode,
      ChartValueMode.Absolute
    )
  }
)

const ChartDisplaySchema = section('Display', 'How the chart looks', {
  title: section('Title', 'The title inside the chart (the widget title is separate)', {
    show: bool('Show title', 'Draw a title inside the chart', false),
    text: str('Text', 'Title text; empty uses the widget title'),
    subtext: str('Subtitle', 'Smaller text under the title'),
    left: oneOf('Align', 'Horizontal position of the title', ChartAlign, ChartAlign.Center),
    fontSize: num('Font size', 'Title font size, px', 16, 8, 48)
  }),
  legend: section('Legend', 'The list of series or categories with their colors', {
    show: bool('Show legend', 'Draw the legend', true),
    type: oneOf('Type', 'scroll pages a long legend instead of wrapping it', ChartLegendType, ChartLegendType.Scroll),
    position: oneOf('Position', 'Side of the chart the legend sits on', ChartSide, ChartSide.Bottom),
    align: oneOf('Icon side', 'Side of the text the color icon sits on', ChartLegendAlign, ChartLegendAlign.Auto),
    icon: oneOf('Icon', 'Shape of the color icon', ChartLegendIcon, ChartLegendIcon.RoundRect),
    fontSize: num('Font size', 'Legend font size, px', 12, 8, 24)
  }),
  tooltip: section('Tooltip', 'The box shown on hover', {
    show: bool('Show tooltip', 'Show a tooltip on hover', true),
    trigger: oneOf(
      'Trigger',
      'item: one bar / point / slice; axis: every series at the hovered category',
      ChartTooltipTrigger,
      ChartTooltipTrigger.Axis
    ),
    axisPointer: oneOf(
      'Axis pointer',
      'With the axis trigger, how the hovered category is marked',
      ChartAxisPointer,
      ChartAxisPointer.Shadow,
      onlyFor(AXIS_CHARTS)
    ),
    showTotal: bool(
      'Show total',
      'With the axis trigger, add the total of all series to the tooltip',
      false,
      onlyFor(AXIS_CHARTS)
    )
  }),
  colors: section('Colors', 'Series and category colors', {
    palette: oneOf(
      'Palette',
      'Colors used in turn by series (or by categories, see byCategory)',
      ChartPalette,
      ChartPalette.Default
    ),
    custom: z
      .array(z.string().regex(HEX_COLOR))
      .max(CUSTOM_COLORS_MAX)
      .default([])
      .meta({
        title: 'Custom colors',
        description: 'With the CUSTOM palette: hex colors used in turn, e.g. ["#5470c6", "#91cc75"]',
        [SCHEMA_KEYS.widget]: SchemaWidget.Colors
      }),
    byCategory: bool(
      'Color each category',
      'With one series, give every bar / point its own color instead of one color for the series',
      false,
      onlyFor(AXIS_CHARTS)
    ),
    useFieldColors: bool(
      'Use field colors',
      'When grouping by a risk matrix, color each rating as in the matrix (e.g. High red) instead of the palette',
      true
    )
  }),
  labels: section('Data labels', 'Values written on the bars, points or slices', {
    show: bool('Show labels', 'Write a label on every bar / point / slice', false),
    position: oneOf(
      'Position',
      'Where the label sits. Bars: top, inside, insideTop, insideBottom, right, left. Pie: outside, inside',
      ChartLabelPosition,
      ChartLabelPosition.Top
    ),
    format: oneOf(
      'Content',
      'VALUE, PERCENT (share of the series; in a bar stacked to 100%, of the category), NAME (category), or the name with the value / percent',
      ChartLabelFormat,
      ChartLabelFormat.Value
    ),
    fontSize: num('Font size', 'Label font size, px', 12, 8, 24),
    rotate: num('Rotate', 'Label rotation, degrees', 0, -90, 90)
  }),
  number: section('Number format', 'How values are written in labels, tooltips and the value axis', {
    decimals: num('Decimals', 'Digits after the decimal point, in values and in the percent of labels', 0, 0, 6),
    prefix: str('Prefix', 'Written before the value, e.g. "$"', ''),
    suffix: str('Suffix', 'Written after the value, e.g. " h"', ''),
    compact: bool(
      'Compact',
      'Shorten large numbers: 1 200 → 1.2K, 3 400 000 → 3.4M, 5 000 000 000 → 5B. Numbers under 1 000 stay as they are',
      false
    )
  }),
  xAxis: section(
    'Category axis',
    'The axis of the categories (horizontal unless the bars are horizontal)',
    {
      show: bool('Show axis', 'Draw the category axis', true),
      name: str('Name', 'Axis name'),
      nameLocation: oneOf('Name position', 'Where the axis name sits', ChartNameLocation, ChartNameLocation.Middle),
      labelRotate: num('Label rotate', 'Category label rotation, degrees', 0, -90, 90),
      labelInterval: oneOf(
        'Labels',
        'auto skips labels that overlap; all writes every label',
        ChartLabelInterval,
        ChartLabelInterval.Auto
      ),
      labelMaxLength: num('Label max length', 'Cut longer category labels with "…"; 0 never cuts', 20, 0, 200),
      splitLine: bool('Grid lines', 'Draw a line for every category', false),
      inverse: bool('Reverse', 'Draw the categories in the reverse order', false)
    },
    onlyFor(AXIS_CHARTS)
  ),
  yAxis: section(
    'Value axis',
    'The axis of the values',
    {
      show: bool('Show axis', 'Draw the value axis', true),
      name: str('Name', 'Axis name'),
      nameLocation: oneOf('Name position', 'Where the axis name sits', ChartNameLocation, ChartNameLocation.End),
      type: oneOf('Scale', 'value: linear; log: logarithmic (values must be positive)', ChartScale, ChartScale.Value),
      min: optionalNum('Min', 'Lowest value of the axis; empty fits the data'),
      max: optionalNum('Max', 'Highest value of the axis; empty fits the data'),
      splitLine: bool('Grid lines', 'Draw horizontal grid lines', true),
      inverse: bool('Reverse', 'Grow the values the other way', false)
    },
    onlyFor(AXIS_CHARTS)
  ),
  grid: section(
    'Margins',
    'Space between the plot and the widget edges, px; labels of the axes fit inside',
    {
      top: num('Top', 'Top margin, px', 40, 0, 300),
      right: num('Right', 'Right margin, px', 24, 0, 300),
      bottom: num('Bottom', 'Bottom margin, px', 40, 0, 300),
      left: num('Left', 'Left margin, px', 24, 0, 300)
    },
    onlyFor(AXIS_CHARTS)
  ),
  bar: section(
    'Bars',
    'Bar chart options',
    {
      horizontal: bool('Horizontal', 'Draw the bars horizontally, the categories on the vertical axis', false),
      stack: oneOf(
        'Stack',
        'With two group-by fields: NONE puts series side by side, STACKED on top of each other, PERCENT stacks them to 100%',
        ChartStack,
        ChartStack.None
      ),
      barWidth: optionalNum('Bar width', 'Bar width, px; empty fits the space', 1, 200),
      barMaxWidth: num('Max bar width', 'Bars never get wider, px', 60, 1, 200),
      barGap: num('Gap between series', 'Gap between the bars of one category, % of the bar width', 20, -100, 100),
      borderRadius: num('Corner radius', 'Rounded bar corners, px', 4, 0, 50),
      showBackground: bool('Background', 'Draw a light bar behind every bar, to the axis maximum', false)
    },
    onlyFor([ChartType.Bar])
  ),
  line: section(
    'Lines',
    'Line chart options',
    {
      smooth: bool('Smooth', 'Draw curves instead of straight segments', false),
      area: bool('Area', 'Fill the area under the line', false),
      areaOpacity: num('Area opacity', 'Opacity of the area fill, 0–1', 0.3, 0, 1),
      step: oneOf(
        'Step',
        'Draw a step line; the step comes at the start, middle or end of a period',
        ChartLineStep,
        ChartLineStep.None
      ),
      showSymbol: bool('Points', 'Mark every point', true),
      symbol: oneOf('Point shape', 'Shape of the point marks', ChartSymbol, ChartSymbol.EmptyCircle),
      symbolSize: num('Point size', 'Size of the point marks, px', 6, 1, 40),
      lineWidth: num('Line width', 'Line width, px', 2, 0, 20),
      stack: bool('Stack', 'With two group-by fields, stack the series on top of each other', false),
      connectNulls: bool('Connect gaps', 'Draw the line over categories with no value', false)
    },
    onlyFor([ChartType.Line])
  ),
  pie: section(
    'Pie',
    'Pie chart options',
    {
      variant: oneOf(
        'Variant',
        'PIE; DONUT (a hole in the middle); ROSE_RADIUS / ROSE_AREA (Nightingale rose: slice radius / area shows the value)',
        ChartPieVariant,
        ChartPieVariant.Pie
      ),
      innerRadius: num('Inner radius', 'Hole radius for DONUT and rose variants, % of the space', 40, 0, 95),
      outerRadius: num('Outer radius', 'Pie radius, % of the space', 70, 5, 100),
      centerX: num('Center X', 'Horizontal center, % of the width', 50, 0, 100),
      centerY: num('Center Y', 'Vertical center, % of the height', 50, 0, 100),
      startAngle: num('Start angle', 'Angle of the first slice, degrees', 90, 0, 360),
      clockwise: bool('Clockwise', 'Lay the slices out clockwise', true),
      padAngle: num('Gap between slices', 'Gap between slices, degrees', 0, 0, 20),
      borderRadius: num('Corner radius', 'Rounded slice corners, px', 4, 0, 50),
      minAngle: num('Min slice angle', 'Smallest slice angle, degrees, so tiny slices stay visible', 0, 0, 90),
      labelLine: bool('Label lines', 'Lines from outside labels to their slices', true),
      selectedMode: oneOf(
        'Selection',
        'A click pulls a slice out: none, one at a time, or several',
        ChartSelectedMode,
        ChartSelectedMode.None
      )
    },
    onlyFor([ChartType.Pie])
  ),
  markLines: section(
    'Reference lines',
    'Lines across the plot at the series average, minimum or maximum',
    {
      average: bool('Average', 'Line at the average of each series', false),
      min: bool('Minimum', 'Line at the minimum of each series', false),
      max: bool('Maximum', 'Line at the maximum of each series', false)
    },
    onlyFor(AXIS_CHARTS)
  )
})

export const ChartConfigSchema = z
  .strictObject({
    version: z.literal(1).default(1).meta({ description: 'Format version of this config' }),
    type: z.enum(ChartType).meta({
      description: 'BAR and LINE group by one or two fields, PIE by one (GROUP_BY_COUNTS)'
    }),
    transform: ChartTransformSchema,
    display: ChartDisplaySchema
  })
  .meta({
    title: 'Chart',
    description:
      'A saved chart, stored as Chart.config and shown on dashboards by chartId; what it counts is Chart.query. The ' +
      'app reads the rows of chartData(chartId) and draws them with ECharts: transform shapes the rows, display ' +
      'styles the chart.'
  })

export type ChartConfig = z.output<typeof ChartConfigSchema>
